import { config } from '@mfw/translations';

import type {
  FetchMembershipClientsArgs,
  AssignClientMembershipArgs,
  AssignMembershipBulkArgs,
  ActivateRecurringMembershipArgs,
  ResendCardSetupRequestArgs,
  ScheduleMembershipActivationArgs,
  CancelClientMembershipArgs,
  EnableClientMembershipArgs,
  FreezeClientMembershipArgs,
  RenewClientMembershipArgs,
  UndoMembershipRenewalArgs,
  EndClientMembershipCycleArgs,
  ReplaceClientMembershipNowArgs,
  FetchClientMembershipsArgs,
  FetchMembershipStatsArgs,
  EditClientMembershipArgs,
  FetchMembershipByIdArgs,
  ApiClientMembershipsEnvelope,
  ApiMembershipStatsResult,
  ApiMembershipStats,
  CreateMembershipArgs,
  ApiMembershipClient,
  FetchMembershipsArgs,
  EditMembershipArgs,
  ApiUserMembership,
  CreateCheckInArgs,
  FetchCheckInsArgs,
  ApiCheckIn,
  VoidCheckInArgs,
  VoidPaymentArgs,
  AddPaymentArgs,
  SendManualRenewalNotificationArgs,
  ApiSendManualRenewalNotificationResult,
  AddCycleDiscountArgs,
} from './types/memberships.types';
import type {
  UiMembershipRecord,
  ApiMembership,
} from '../schemas/memberships.schema';
import type { BaseApiResponse } from './types/api.types';
import type { LedgerCoverage } from '@mfw/common';

import { destroy, patch, post, get } from './axios';

function mapMembershipFromApi(
  api: ApiMembership,
  locale: string,
): UiMembershipRecord {
  return {
    id: api.id,
    authorId: api.authorId ?? null,
    collaborationModeA:
      api.collaborationModeA as UiMembershipRecord['collaborationModeA'],
    createdAt: api.createdAt,
    currency: api.currency as UiMembershipRecord['currency'],
    description: api.description ?? undefined,
    duration: api.duration,
    durationUnit: api.durationUnit as UiMembershipRecord['durationUnit'],
    includesTrainer: api.includesTrainer ?? false,
    name: api.name,
    numberOfWorkouts: api.numberOfWorkouts,
    organizationId: api.organizationId,
    price: api.price,
    status: api.status,
    tagIds: api.tags?.map((t) => t.id) ?? [],
    termsLocked: api.termsLocked,
    // absent stays absent (withheld from a non-manager); null and zero kept
    trainerFeeType: api.trainerFeeType,
    trainerFeeValue: api.trainerFeeValue,
    tags:
      api.tags?.map((t) => ({
        id: t.id,
        categoryId: t.category?.id ?? t.categoryId,
        categoryName:
          t.category?.translations?.find((tr) => tr.locale === locale)?.name ||
          undefined,
        name:
          t.translations?.find((tr) => tr.locale === locale)?.name ||
          t.name ||
          t.id,
      })) ?? [],
    updatedAt: api.updatedAt,
    usersCount: api.usersCount,
  };
}

export const fetchMemberships = async ({
  locale,
  organizationId,
  status,
}: FetchMembershipsArgs) => {
  const response = await get<BaseApiResponse<ApiMembership[]>>(
    `/v2/organizations/${organizationId}/memberships`,
    { query: status ? { status } : undefined },
  );
  return (response?.data ?? []).map((m) => mapMembershipFromApi(m, locale));
};

export const fetchMembershipById = async ({
  locale,
  membershipId,
  organizationId,
}: FetchMembershipByIdArgs) => {
  const response = await get<BaseApiResponse<ApiMembership>>(
    `/v2/organizations/${organizationId}/memberships/${membershipId}`,
  );
  if (!response?.data) return null;
  return mapMembershipFromApi(response.data, locale ?? config.defaultLocale);
};

export const createMembership = async ({
  collaborationModeA,
  currency,
  description,
  duration,
  durationUnit,
  includesTrainer,
  locale,
  name,
  numberOfWorkouts,
  organizationId,
  price,
  tagIds,
  trainerFeeType,
  trainerFeeValue,
}: CreateMembershipArgs) => {
  // undefined fee keys drop out of the JSON body (fee left untouched)
  const response = await post<ApiMembership>(
    `/v2/organizations/${organizationId}/memberships`,
    {
      collaborationModeA,
      currency,
      description,
      duration,
      durationUnit,
      includesTrainer,
      name,
      numberOfWorkouts,
      price,
      tagIds,
      trainerFeeType,
      trainerFeeValue,
    },
  );
  if (!response?.data) return null;
  return mapMembershipFromApi(response.data, locale);
};

export const editMembership = async ({
  collaborationModeA,
  currency,
  description,
  duration,
  durationUnit,
  includesTrainer,
  locale,
  membershipId,
  name,
  numberOfWorkouts,
  organizationId,
  price,
  tagIds,
  trainerFeeType,
  trainerFeeValue,
}: EditMembershipArgs) => {
  // undefined fee keys drop out of the JSON body (fee left untouched)
  const response = await patch<ApiMembership>(
    `/v2/organizations/${organizationId}/memberships/${membershipId}`,
    {
      collaborationModeA,
      currency,
      description,
      duration,
      durationUnit,
      includesTrainer,
      name,
      numberOfWorkouts,
      price,
      tagIds,
      trainerFeeType,
      trainerFeeValue,
    },
  );
  if (!response?.data) return null;
  return mapMembershipFromApi(response.data, locale);
};

export const archiveMembership = async ({
  locale,
  membershipId,
  organizationId,
}: FetchMembershipByIdArgs) => {
  const response = await patch<ApiMembership>(
    `/v2/organizations/${organizationId}/memberships/${membershipId}/archive`,
  );
  if (!response?.data) return null;
  return mapMembershipFromApi(response.data, locale ?? config.defaultLocale);
};

export const unarchiveMembership = async ({
  locale,
  membershipId,
  organizationId,
}: FetchMembershipByIdArgs) => {
  const response = await patch<ApiMembership>(
    `/v2/organizations/${organizationId}/memberships/${membershipId}/unarchive`,
  );
  if (!response?.data) return null;
  return mapMembershipFromApi(response.data, locale ?? config.defaultLocale);
};

export const deleteMembership = async ({
  membershipId,
  organizationId,
}: FetchMembershipByIdArgs) => {
  return destroy(
    `/v2/organizations/${organizationId}/memberships/${membershipId}`,
  );
};

export const fetchMembershipStats = async ({
  organizationId,
}: FetchMembershipStatsArgs): Promise<ApiMembershipStatsResult> => {
  const response = await get<
    BaseApiResponse<ApiMembershipStats[]> & { coverage?: LedgerCoverage }
  >(`/v2/organizations/${organizationId}/memberships/stats`);
  return { coverage: response?.coverage, rows: response?.data ?? [] };
};

export const fetchMembershipClients = async ({
  membershipId,
  organizationId,
}: FetchMembershipClientsArgs) => {
  const response = await get<BaseApiResponse<ApiMembershipClient[]>>(
    `/v2/organizations/${organizationId}/memberships/${membershipId}/clients`,
  );
  return response?.data ?? [];
};

/**
 * T10 (spec §3.3): the ENVELOPE is the answer, not just its rows —
 * `summary`/`coverage` describe exactly the cohort `data` holds, and they are
 * ABSENT for a reader without money rights, so this must not unwrap `.data`.
 *
 * The filters are optional and omitted when unset: no filters is the complete
 * list, which is what every caller that only wants rows still asks for. Array
 * values go out as REPEATED keys (`queryParamsFromString`), which is what the
 * route's DTO reads; an empty array is dropped by the same serializer, so an
 * empty type selection correctly means "all types".
 */
export const fetchClientMemberships = async ({
  clientId,
  from,
  membershipIds,
  organizationId,
  to,
}: FetchClientMembershipsArgs) => {
  const response = await get<ApiClientMembershipsEnvelope>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships`,
    { query: { from, membershipIds, to } },
  );
  return {
    data: response?.data ?? [],
    ...(response?.summary ? { summary: response.summary } : {}),
    ...(response?.coverage ? { coverage: response.coverage } : {}),
  };
};

export const editClientMembership = async ({
  autoRenew,
  clientId,
  notes,
  organizationId,
  startDate,
  trainerFeeType,
  trainerFeeValue,
  trainerId,
  userMembershipId,
}: EditClientMembershipArgs) => {
  // Undefined keys drop out of the JSON body: an absent `startDate` moves
  // nothing, an absent `trainerId` keeps the stored trainer, an absent
  // `autoRenew` leaves the preference untouched, and an absent fee pair
  // keeps the stored fee (MV2-577). The route is strict — never add package
  // or price keys here (Celina 1.5).
  const response = await patch<{ data: ApiUserMembership }>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}`,
    {
      autoRenew,
      notes,
      startDate,
      trainerFeeType,
      trainerFeeValue,
      trainerId,
    },
  );
  return response?.data ?? null;
};

/**
 * T6 (D9): the cancellation body is EMPTY. The route's DTO is strict, so a
 * leftover `refundAmount` would now be a 400 — which is the point: a refund
 * is an explicit, attested operation of its own (`POST .../refunds`).
 */
export const cancelClientMembership = async ({
  clientId,
  organizationId,
  userMembershipId,
}: CancelClientMembershipArgs) => {
  const response = await patch<{ data: { id: string } }>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/cancel`,
    {},
  );
  return response?.data ?? null;
};

export const freezeClientMembership = async ({
  clientId,
  freezeStartDate,
  frozenDays,
  organizationId,
  userMembershipId,
}: FreezeClientMembershipArgs) => {
  const response = await patch<{ data: { id: string } }>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/freeze`,
    { freezeStartDate, frozenDays },
  );
  return response?.data ?? null;
};

/** STAFF role plan Phase 3 — the manual renewal-notification race-lock CTA. */
export const sendManualRenewalNotification = async ({
  clientId,
  organizationId,
  userMembershipId,
}: SendManualRenewalNotificationArgs) => {
  const response = await patch<{
    data: ApiSendManualRenewalNotificationResult;
  }>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/manual-renewal-notification`,
  );
  return response?.data ?? null;
};

/**
 * MV2-573 §1.2: a trainer letting a PENDING_PAYMENT_METHOD membership run
 * before its card actually cleared. Same empty-body shape as
 * `cancelClientMembership`.
 */
export const enableClientMembership = async ({
  clientId,
  organizationId,
  userMembershipId,
}: EnableClientMembershipArgs) => {
  const response = await patch<{ data: { id: string } }>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/enable`,
    {},
  );
  return response?.data ?? null;
};

// MV2-573 §2: the client's own card must already exist (self-or-staff
// endpoint; NO_SAVED_CARD → the FE walks the card-setup redirect first via
// `usePurchaseWithCardSetup`, same contract as premium's subscribe).
export const activateRecurringMembership = async ({
  clientId,
  organizationId,
  userMembershipId,
}: ActivateRecurringMembershipArgs) => {
  const response = await post<{ data: { subscriptionId: string } }>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/activate-recurring`,
    {},
  );
  return response?.data ?? null;
};

// Staff send the card-setup request again to a client whose card-now
// membership still waits for its card; the 72h window restarts.
export const resendCardSetupRequest = async ({
  clientId,
  organizationId,
  userMembershipId,
}: ResendCardSetupRequestArgs) => {
  const response = await post<{ data: { id: string } }>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/card-setup-request`,
    {},
  );
  return response?.data ?? null;
};

// MV2-573 §1.2: a trainer scheduling the first automatic charge attempt for
// a RECURRING membership run "on trust".
export const scheduleMembershipActivation = async ({
  clientId,
  date,
  organizationId,
  userMembershipId,
}: ScheduleMembershipActivationArgs) => {
  const response = await patch<{
    data: { id: string; scheduledActivationDate: string | null };
  }>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/schedule-activation`,
    { date },
  );
  return response?.data ?? null;
};

/**
 * Task 3 (membership-renewal plan) — "Obnovi istu". A 409 with
 * `MEMBERSHIP_ALREADY_RENEWED` means another renewal (or Replace) landed
 * first; the dialog says so inline.
 */
export const renewClientMembership = async ({
  clientId,
  discountPercent,
  numberOfWorkouts,
  organizationId,
  startDate,
  userMembershipId,
}: RenewClientMembershipArgs) => {
  // `post<T>` resolves to the response body, as for `assignClientMembership`;
  // plan 1.4 — a correction only when the dialog sent one (the DTO is strict)
  const response = await post<ApiUserMembership>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/renew`,
    {
      startDate,
      ...(numberOfWorkouts === undefined ? {} : { numberOfWorkouts }),
      ...(discountPercent === undefined ? {} : { discountPercent }),
    },
  );
  return response?.data ?? null;
};

/**
 * Task 7 (membership-renewal plan) — "Poništi obnovu": removes an automatic
 * renewal nothing has touched and switches the previous row's auto-renew
 * off. A used one answers 400 ("end the cycle instead"), toasted as usual.
 */
export const undoClientMembershipRenewal = async ({
  clientId,
  organizationId,
  userMembershipId,
}: UndoMembershipRenewalArgs) => {
  await destroy(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/renewal`,
  );
};

/**
 * Task 9 (membership-renewal plan) — "End cycle": ends the cycle on the
 * organization's today, for the whole package or reduced to an amount.
 */
export const endClientMembershipCycle = async ({
  clientId,
  oldCycle,
  organizationId,
  userMembershipId,
}: EndClientMembershipCycleArgs) => {
  const response = await post<ApiUserMembership>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/end-cycle`,
    oldCycle,
  );
  return response?.data ?? null;
};

/**
 * Task 9 — "Replace now": ends this cycle today and starts the new package
 * today, in ONE call (the server does both in one transaction). A 409 with
 * `MEMBERSHIP_ALREADY_RENEWED` means the cycle already has a successor.
 */
export const replaceClientMembershipNow = async ({
  autoRenew,
  clientId,
  discountPercent,
  membershipId,
  notes,
  numberOfWorkouts,
  oldCycle,
  organizationId,
  requiresCardLinkNow,
  trainerFeeType,
  trainerFeeValue,
  trainerId,
  type,
  userMembershipId,
}: ReplaceClientMembershipNowArgs) => {
  // undefined fee keys drop out of the JSON body (the new package's default)
  const response = await post<ApiUserMembership>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/replace-now`,
    {
      autoRenew,
      discountPercent,
      membershipId,
      notes,
      numberOfWorkouts,
      oldCycle,
      requiresCardLinkNow,
      trainerFeeType,
      trainerFeeValue,
      trainerId,
      type,
    },
  );
  return response?.data ?? null;
};

export const assignClientMembership = async ({
  autoRenew,
  clientId,
  discountPercent,
  membershipId,
  notes,
  numberOfWorkouts,
  organizationId,
  replacesUserMembershipId,
  requiresCardLinkNow,
  startDate,
  trainerFeeType,
  trainerFeeValue,
  trainerId,
  type,
}: AssignClientMembershipArgs) => {
  // `post<T>` already resolves to `BaseApiResponse<T>` and returns its body, so
  // the payload type goes in directly — wrapping it in another `{ data }` types
  // the result one envelope too deep and every `.data` read off it is undefined.
  const response = await post<ApiUserMembership>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships`,
    {
      autoRenew,
      discountPercent,
      membershipId,
      notes,
      numberOfWorkouts,
      replacesUserMembershipId,
      requiresCardLinkNow,
      startDate,
      // undefined fee keys drop out of the JSON body (the package default)
      trainerFeeType,
      trainerFeeValue,
      trainerId,
      type,
    },
  );
  return response?.data ?? null;
};

export const assignMembershipBulk = async ({
  autoRenew,
  clientIds,
  discountPercent,
  groupIds,
  membershipId,
  notes,
  numberOfWorkouts,
  organizationId,
  requiresCardLinkNow,
  startDate,
  type,
}: AssignMembershipBulkArgs) => {
  const response = await post<{
    data: { assignedCount: number; clientIds: string[] };
  }>(
    `/v2/organizations/${organizationId}/memberships/${membershipId}/clients`,
    {
      autoRenew,
      clientIds,
      discountPercent,
      groupIds,
      notes,
      numberOfWorkouts,
      requiresCardLinkNow,
      startDate,
      type,
    },
  );
  return response?.data ?? null;
};

export const addPayment = async ({
  amount,
  clientId,
  currency,
  note,
  organizationId,
  paidAt,
  paymentType,
  userMembershipId,
}: AddPaymentArgs) => {
  const response = await post(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/payments`,
    {
      amount,
      currency,
      notes: note,
      paymentDate: paidAt,
      paymentMethod: paymentType,
    },
  );
  return response?.data ?? null;
};

/**
 * Task 8 (membership-renewal plan) — records the rest of a short payment as
 * a discount. The server refuses (400, toasted) more than is still due, a
 * card-managed cycle and one whose payment state it cannot state exactly.
 */
export const addCycleDiscount = async ({
  amount,
  clientId,
  organizationId,
  reason,
  userMembershipId,
}: AddCycleDiscountArgs) => {
  const response = await post(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/cycle-discounts`,
    { amount, reason },
  );
  return response?.data ?? null;
};

// void an incorrectly entered payment (R-AUD-47) — BE does a soft-void +
// mirror refund row; the payment disappears from the list, stats are adjusted
export const voidPayment = async ({
  clientId,
  organizationId,
  paymentId,
  userMembershipId,
}: VoidPaymentArgs) => {
  await destroy(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/payments/${paymentId}`,
  );
};

export const createCheckIn = async ({
  bookingId,
  checkedInAt,
  clientId,
  confirmDuplicate,
  consumesVisit,
  isTrial,
  notes,
  organizationId,
  userMembershipId,
}: CreateCheckInArgs) => {
  // The created row — its id is the provenance a locker-key shortcut sends.
  const response = await post<ApiCheckIn>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/check-ins`,
    { bookingId, checkedInAt, confirmDuplicate, consumesVisit, isTrial, notes },
  );
  return response?.data ?? null;
};

export const fetchCheckIns = async ({
  clientId,
  organizationId,
  userMembershipId,
}: FetchCheckInsArgs) => {
  const response = await get<{ data: ApiCheckIn[] }>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/check-ins`,
  );
  return response?.data ?? [];
};

// MV2-573 §4.2: undoes a walk-in check-in — staff-only, soft-void, so the
// audit trail (who/when) survives on the row itself.
export const voidCheckIn = async ({
  checkInId,
  clientId,
  organizationId,
  userMembershipId,
}: VoidCheckInArgs) => {
  await destroy(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/check-ins/${checkInId}`,
  );
};
